/**
 * Background media work (the Library's eager thumbnail backfill: video posters decoded by the browser's media
 * decoders, whole files fetched and decrypted) waits while the page plays video: those decoders and that bandwidth
 * are what the playing clip needs, and sharing them is a stutter in its picture and sound. The renderer that plays
 * video registers a probe (the vidOS engine: a mix channel playing a video); background work asks `mediaQuiet()`
 * before each step and, while it is not, `whenMediaQuiet()` resolves once it is (checked every
 * MEDIA_QUIET_POLL_MS: no subscriptions to keep in step). With no probe registered the page is always quiet.
 */

/** How often a waiting step looks again (ms). */
export const MEDIA_QUIET_POLL_MS = 1000;

type BusyProbe = () => boolean;
let probe: BusyProbe | null = null;

/** The playing renderer's "busy" probe (null: none). Returns its unregister. */
export function setMediaBusyProbe(next: BusyProbe | null): () => void {
  probe = next;
  return () => {
    if (probe === next) probe = null;
  };
}

/** Whether background media work may run now. A probe that throws counts as quiet (never wedges the work). */
export function mediaQuiet(): boolean {
  if (!probe) return true;
  try {
    return !probe();
  } catch {
    return true;
  }
}

/** Resolves once background media work may run (at once when it may now). */
export function whenMediaQuiet(): Promise<void> {
  if (mediaQuiet()) return Promise.resolve();
  return new Promise((resolve) => {
    const look = () => {
      if (mediaQuiet()) resolve();
      else setTimeout(look, MEDIA_QUIET_POLL_MS);
    };
    setTimeout(look, MEDIA_QUIET_POLL_MS);
  });
}
